import { useScrollReveal } from "@/hooks/useScrollReveal";

const BG_IMAGE =
  "https://public.readdy.ai/ai/img_res/0d8dcdc795e0f577417c6e4570719615.jpg";

interface FinalCTAProps {
  title?: string;
  subtitle?: string;
  primaryCTA?: { label: string; href: string };
}

export default function FinalCTA({
  title = "Your Next Vehicle Is Waiting in Farmington",
  subtitle = "Join hundreds of satisfied customers who chose United Auto Group for quality, honesty, and a better car-buying experience. Visit us today — no appointment needed.",
  primaryCTA = {
    label: "Browse Inventory Now",
    href: "https://www.uagmn.com/inventory/",
  },
}: FinalCTAProps) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section className="relative py-28 md:py-36 overflow-hidden" aria-label="Final call to action">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={BG_IMAGE}
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/98 via-charcoal/92 to-charcoal/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-charcoal/50" />
      </div>

      {/* Decorative gold lines */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold-DEFAULT/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold-DEFAULT/30 to-transparent" />

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="inline-flex items-center gap-3 mb-6 justify-center">
          <span className="h-px w-10 bg-gold-DEFAULT" />
          <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
            READY TO DRIVE?
          </span>
          <span className="h-px w-10 bg-gold-DEFAULT" />
        </div>

        <h2
          className="font-serif text-white leading-tight mb-5 mx-auto"
          style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)", lineHeight: 1.05 }}
        >
          {title}
        </h2>

        <p className="text-white font-sans text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-11">
          {subtitle}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-9">
          <a
            href={primaryCTA.href}
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 bg-gold-DEFAULT text-charcoal font-bold text-sm px-12 py-4 rounded-full hover:bg-gold-light transition-all duration-300 whitespace-nowrap cursor-pointer tracking-wide"
          >
            {primaryCTA.label}
            <div className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1 duration-300">
              <i className="ri-arrow-right-line" />
            </div>
          </a>
          <a
            href="https://www.uagmn.com/apply-online/"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-white/35 text-white font-semibold text-sm px-12 py-4 rounded-full hover:border-white/70 hover:bg-white/8 transition-all duration-300 whitespace-nowrap cursor-pointer tracking-wide backdrop-blur-sm"
          >
            Apply for Financing
          </a>
        </div>

        {/* Phone */}
        <a
          href="tel:9527693208"
          className="inline-flex items-center justify-center gap-2.5 text-white hover:text-gold-DEFAULT transition-colors duration-200 cursor-pointer whitespace-nowrap"
          aria-label="Call (952) 769-3208"
        >
          <div className="w-7 h-7 rounded-full border border-gold-DEFAULT/40 flex items-center justify-center">
            <i className="ri-phone-fill text-gold-DEFAULT text-xs" />
          </div>
          <span className="font-sans text-base font-medium">(952) 769-3208 — Farmington, MN</span>
        </a>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-8 mt-11 pt-8 border-t border-white/15">
          {[
            { icon: "ri-shield-check-line", text: "15-Day Warranty" },
            { icon: "ri-star-fill", text: "5.0★ Google Rated" },
            { icon: "ri-bank-card-line", text: "All Credit Welcome" },
            { icon: "ri-map-pin-2-line", text: "Farmington, MN 55024" },
            { icon: "ri-group-line", text: "Family-Owned" },
          ].map((badge) => (
            <div key={badge.text} className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${badge.icon} text-gold-DEFAULT text-sm`} />
              </div>
              <span className="text-white text-xs font-sans whitespace-nowrap tracking-wide">
                {badge.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
